import {
  compositionForMeasurement,
  cellForMeasurement,
  readCompositionAppearance,
} from "../design/catalogue-runtime.js";
import type { InterfaceDesign } from "../design/visual-variants.js";
import type { InterfaceColorMode } from "../design/tokens.js";
import { designTokens } from "../design/tokens.js";
import type { LayoutDocument, LayoutNode } from "../layout/types.js";
import { coerceCustomDisciplineLayoutDocument } from "./layout-documents.js";

/** DataGrid rows share one height; independent header bands use their own maximum. */
export function readCustomDisciplineColumnLayoutHeights(
  fields: ReadonlyArray<{ cellLayout?: unknown }>,
  minimums: { rowHeight?: number; columnHeaderHeight?: number } = {},
  mode: InterfaceDesign = "original",
  color: InterfaceColorMode = "light",
) {
  const cells: number[] = [],
    headers: number[] = [],
    groups: number[] = [];
  const add = (target: number[], value: unknown) => {
    const document = coerceCustomDisciplineLayoutDocument(
      value,
    ) as unknown as LayoutDocument | null;
    if (document)
      target.push(Math.max(24, Math.min(500, document.meta.canvas.height)));
  };
  for (const field of fields) {
    let cell = cellForMeasurement(field.cellLayout, mode, color);
    if (typeof cell === "string") {
      try {
        cell = JSON.parse(cell);
      } catch {
        continue;
      }
    }
    if (
      !cell ||
      typeof cell !== "object" ||
      !(cell as { enabled?: boolean }).enabled
    )
      continue;
    const definition = cell as {
      layout?: unknown;
      headerLayout?: unknown;
      headerGroup?: { layout?: unknown };
    };
    add(cells, definition.layout);
    add(headers, definition.headerLayout);
    add(groups, definition.headerGroup?.layout);
  }
  const maximum = (values: number[], minimum?: number) =>
    values.length ? Math.max(minimum ?? 0, ...values) : minimum;
  return {
    rowHeight: maximum(cells, minimums.rowHeight),
    columnHeaderHeight: maximum(headers, minimums.columnHeaderHeight),
    columnGroupHeaderHeight: maximum(groups, minimums.columnHeaderHeight),
  };
}

/** Stable Display Editor elements used as table style samples, independent of sport. */
const selectors: Record<string, string> = {
  "column-header": "& .MuiDataGrid-columnHeader",
  cell: "& .MuiDataGrid-cell",
  "hover-row": "& .MuiDataGrid-row:hover",
  "selected-row": "& .MuiDataGrid-row.Mui-selected",
  "hover-selected-row": "& .MuiDataGrid-row.Mui-selected:hover",
  "hover-active-row":
    "& .MuiDataGrid-row.custom-current-athlete-row:hover, & .MuiDataGrid-row.custom-current-group-row:hover",
  "hover-cell": "& .MuiDataGrid-cell:hover",
  "readonly-cell":
    "& .MuiDataGrid-cell:not(.MuiDataGrid-cell--editable):not(.custom-discipline-checkbox-cell), & .custom-discipline-readonly-cell",
  "editable-cell": "& .MuiDataGrid-cell--editable",
  "editing-cell": "& .MuiDataGrid-cell--editing",
  "active-row": "& .custom-current-athlete-row, & .custom-current-group-row",
  "selected-cell":
    "& .custom-selected-cell, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within",
  toolbar: "& .MuiDataGrid-toolbarContainer",
  footer: "& .MuiDataGrid-footerContainer",
};
const allowedStyles = new Set([
  "backgroundColor",
  "color",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "textAlign",
  "border",
  "borderTop",
  "borderRight",
  "borderBottom",
  "borderLeft",
  "borderColor",
  "borderRadius",
  "padding",
  "paddingLeft",
  "paddingRight",
  "outline",
  "outlineOffset",
  "boxShadow",
  "cursor",
]);

export function readCustomDisciplineGridPresentation(
  value: unknown,
  fields: ReadonlyArray<{ id: string; key: string }> = [],
  mode: InterfaceDesign = "original",
  color: InterfaceColorMode = "light",
) {
  const document = coerceCustomDisciplineLayoutDocument(
    compositionForMeasurement(value, mode, color),
  ) as unknown as LayoutDocument | null;
  const sx: Record<string, Record<string, unknown>> = {};
  const hasModernAppearance = Boolean(
    document && readCompositionAppearance(document, mode, color),
  );
  if (hasModernAppearance && document) {
    const palette = designTokens[color];
    sx["&"] = {
      backgroundColor: document.scene.backgroundColor ?? palette.surface,
      color: palette.text,
      borderColor: palette.border,
      "--DataGrid-rowBorderColor": palette.fine,
    };
  } else if (document && mode === "modern") {
    // Transparent authored cells must not inherit a dark application backdrop.
    sx["&"] = {
      backgroundColor:
        document.scene.backgroundColor ?? designTokens.light.surface,
      color: designTokens.light.text,
    };
  }
  let rowHeight: number | undefined;
  let columnHeaderHeight: number | undefined;
  const visit = (node: LayoutNode) => {
    const column = node.id.startsWith("column:")
      ? fields.find((field) => field.key === node.id.slice("column:".length))
      : undefined;
    // Resolve stable admin keys to database IDs, including after JSON import.
    const escapedField = column?.id.replace(
      /[^a-zA-Z0-9_-]/g,
      (char) => `\\${char.codePointAt(0)!.toString(16)} `,
    );
    const selector = column
      ? `& .MuiDataGrid-cell[data-field="${escapedField}"], & .MuiDataGrid-columnHeader[data-field="${escapedField}"]`
      : selectors[node.id];
    if (selector) {
      sx[selector] = Object.fromEntries(
        Object.entries(node.style ?? {}).filter(([key]) =>
          allowedStyles.has(key),
        ),
      );
      if (node.id === "cell" && Number.isFinite(node.height))
        rowHeight = Math.max(24, Math.min(160, node.height));
      if (node.id === "column-header" && Number.isFinite(node.height))
        columnHeaderHeight = Math.max(24, Math.min(160, node.height));
    }
    if ("children" in node) node.children.forEach(visit);
  };
  document?.scene.children.forEach(visit);
  return { sx, rowHeight, columnHeaderHeight, hasModernAppearance };
}

export function createCustomDisciplineGridPresentation(
  name: string,
): LayoutDocument {
  const samples = [
    [
      "column-header",
      "En-tête de colonne",
      60,
      {
        backgroundColor: "rgb(250, 191, 143)",
        fontSize: 12,
        color: "#000000",
        borderRight: "1px solid rgba(0,0,0,0.2)",
      },
    ],
    [
      "cell",
      "Cellule",
      52,
      {
        fontSize: 14,
        color: "#000000",
        borderRight: "1px solid rgba(0,0,0,0.2)",
      },
    ],
    [
      "active-row",
      "Ligne en cours",
      52,
      { backgroundColor: "rgba(25,118,210,0.08)" },
    ],
    [
      "selected-cell",
      "Cellule sélectionnée",
      52,
      { boxShadow: "inset 0 0 0 1px #1976d2" },
    ],
    [
      "hover-row",
      "Ligne survolée",
      52,
      { backgroundColor: "rgba(0,0,0,0.04)" },
    ],
    ["hover-cell", "Cellule survolée", 52, {}],
    [
      "selected-row",
      "Ligne sélectionnée",
      52,
      { backgroundColor: "rgba(25,118,210,0.08)" },
    ],
    [
      "hover-selected-row",
      "Ligne sélectionnée survolée",
      52,
      { backgroundColor: "rgba(25,118,210,0.12)" },
    ],
    [
      "hover-active-row",
      "Ligne en cours survolée",
      52,
      { backgroundColor: "rgba(25,118,210,0.08)" },
    ],
    ["readonly-cell", "Cellule calculée ou non modifiable", 52, {}],
    ["editable-cell", "Cellule modifiable", 52, {}],
    [
      "editing-cell",
      "Cellule en cours de saisie",
      52,
      { backgroundColor: "#ffffff" },
    ],
    ["toolbar", "Barre d’actions", 52, { backgroundColor: "#ffffff" }],
    ["footer", "Pied de grille", 52, { backgroundColor: "#ffffff" }],
  ] as const;
  return {
    version: 1,
    meta: {
      id: "custom-grid-presentation",
      name,
      type: "display",
      canvas: { width: 600, height: 20 + samples.length * 76 },
      createdAt: "2026-10-19T00:00:00.000Z",
      updatedAt: "2026-10-19T00:00:00.000Z",
    },
    scene: {
      backgroundColor: "#ffffff",
      children: samples.map(([id, label, height, style], index) => ({
        id,
        label,
        kind: "primitive",
        primitive: "text",
        text: label,
        x: 24,
        y: 20 + index * 76,
        width: 552,
        height,
        zIndex: 1,
        style: { ...style, textAlign: "left" },
      })) as LayoutNode[],
    },
  };
}

type GroupGridRow = {
  id: string;
  customDisciplineGroup?: { groupTypeId?: string } | null;
};
type GroupGridSchema = {
  id: string;
  displayLayouts?: unknown;
  fields?: ReadonlyArray<{
    scope?: string;
    isVisible?: boolean;
    cellLayout?: unknown;
  }>;
};

/** Common headers can follow one schema only when every displayed row uses it. */
export function sharedCustomDisciplineGroupTypeId(
  rows: readonly GroupGridRow[],
) {
  const id = rows[0]?.customDisciplineGroup?.groupTypeId;
  return id &&
    rows.every((row) => row.customDisciplineGroup?.groupTypeId === id)
    ? id
    : null;
}

/** A custom group's styles must never recolour a legacy row or another schema. */
export function readCustomDisciplineGroupGridPresentation(
  rows: readonly GroupGridRow[],
  schemas: readonly GroupGridSchema[],
  mode: InterfaceDesign = "original",
  color: InterfaceColorMode = "light",
) {
  const usedTypes = new Set(
    rows.map((row) => row.customDisciplineGroup?.groupTypeId).filter(Boolean),
  );
  const byType = new Map(
    schemas
      .filter((schema) => usedTypes.has(schema.id))
      .map((schema) => {
        let layouts: Record<string, unknown> = {};
        try {
          const value =
            typeof schema.displayLayouts === "string"
              ? JSON.parse(schema.displayLayouts)
              : schema.displayLayouts;
          if (value && typeof value === "object")
            layouts = value as Record<string, unknown>;
        } catch {
          /* Use the standard grid until this schema can be loaded. */
        }
        const presentation = readCustomDisciplineGridPresentation(
          layouts.group_grid,
          [],
          mode,
          color,
        );
        const heights = readCustomDisciplineColumnLayoutHeights(
          schema.fields?.filter(
            (field) => field.scope === "GROUP" && field.isVisible !== false,
          ) ?? [],
          presentation,
          mode,
          color,
        );
        return [
          schema.id,
          {
            ...presentation,
            rowHeight: heights.rowHeight,
            columnHeaderHeight: heights.columnHeaderHeight,
          },
        ] as const;
      }),
  );
  const commonId = sharedCustomDisciplineGroupTypeId(rows);
  const common = commonId ? byType.get(commonId) : undefined;
  if (common) return { ...common, rowHeights: {} as Record<string, number> };
  const sx: Record<string, Record<string, unknown>> = {};
  const rowHeights: Record<string, number> = {};
  for (const row of rows) {
    const presentation = byType.get(
      row.customDisciplineGroup?.groupTypeId ?? "",
    );
    if (!presentation) continue;
    if (presentation.rowHeight) rowHeights[row.id] = presentation.rowHeight;
    const escapedId = row.id.replace(
      /[^a-zA-Z0-9_-]/g,
      (char) => `\\${char.codePointAt(0)!.toString(16)} `,
    );
    const scope = `.MuiDataGrid-row[data-id="${escapedId}"]`;
    for (const [selector, style] of Object.entries(presentation.sx)) {
      const scoped = selector
        .split(",")
        .flatMap((part) => {
          const suffix = part.trim().replace(/^&\s*/, "");
          if (
            /MuiDataGrid-(columnHeader|toolbarContainer|footerContainer)/.test(
              suffix,
            )
          )
            return [];
          if (suffix.startsWith(".MuiDataGrid-row"))
            return [`& ${suffix.replace(".MuiDataGrid-row", scope)}`];
          if (/^\.custom-current-(athlete|group)-row$/.test(suffix))
            return [`& ${scope}${suffix}`];
          return [`& ${scope} ${suffix}`];
        })
        .join(", ");
      if (scoped) sx[scoped] = { ...sx[scoped], ...style };
    }
  }
  return {
    sx,
    rowHeights,
    rowHeight: undefined as number | undefined,
    columnHeaderHeight: undefined as number | undefined,
  };
}
